import { useEffect, useState } from 'react';

import useAuth from '@frolight/hooks/useAuth';
import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import * as gatewayService from '@frolight/services/api/gatewayService';
import * as organisationService from '@frolight/services/api/organisationService';

const usePageData = () => {

  const [ loading, setLoading ] = useState(true);
  const [ data, setData ] = useState(null);
  const [ error, setError ] = useState(null);

  const { auth } = useAuth();

  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  useEffect(() => {
    let mounted = true;

    const fetchOrganisationsAbortController = new AbortController();
    const fetchGatewaysAbortController = new AbortController();

    const runEffect = async () => {
      if (mounted) setLoading(true);

      try {
        let organisations = [];
        let unregisteredGateways = [];

        const fetchedOrganisations = await authenticatedAPIServiceCall(organisationService.getOrganisations, {
          abortController: fetchGatewaysAbortController
        });
        
        if (!fetchedOrganisations?.data.length) {
          throw new Error('NO_ORGANISATION_MEMBERSHIPS');
        }

        const fetchedGateways = await gatewayService.getGateways(
          fetchGatewaysAbortController,
          auth.accessToken
        );

        if (fetchedOrganisations?.data.length && fetchedGateways.data) {
          organisations = fetchedOrganisations.data.map(org => {
            return {
              name: org.name,
              id: org.id,
              gateways: fetchedGateways.data.filter(gateway => gateway.organisationId === org.id)
            }
          });

          unregisteredGateways = fetchedGateways.data.filter(gateway => !gateway.organisationId);
          // Sort alphabetical based on gateway name
          unregisteredGateways.sort((a, b) => {
            return a.name.localeCompare(b.name);
          });
        }

        if (mounted) {
          setData({
            organisations: organisations,
            unregisteredGateways: unregisteredGateways
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          if (mounted) setError(err.message);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (auth.accessToken) {
      runEffect();
    }

    return () => {
      mounted = false;
      fetchOrganisationsAbortController.abort();
      fetchGatewaysAbortController.abort();
    };

  }, [ auth ])

  return { loading, error, data };
};

export default usePageData;